<script setup>
import { ref, watch, onMounted } from "vue";
import Keyboard from "simple-keyboard";
import "simple-keyboard/build/css/index.css";

const props = defineProps({
  input: {
    type: String,
    default: "",
  },
});
const emit = defineEmits(["change", "keyPress"]);
const keyboard = ref(null);

const handleShift = () => {
  const currentLayout = keyboard.value.options.layoutName;
  const shiftToggle = currentLayout === "default" ? "shift" : "default";

  // Update the buttonTheme so that shift is highlighted when active
  if (shiftToggle === "shift") {
    keyboard.value.addButtonTheme("{shift}", "shift-active");
  } else {
    keyboard.value.removeButtonTheme("{shift}", "shift-active");
  }

  keyboard.value.setOptions({
    layoutName: shiftToggle,
  });
};

const onChange = (input) => {
  emit("change", input);
};

const onKeyPress = (button) => {
  emit("keyPress", button);

  if (button === "{shift}") {
    handleShift();
  }
};

watch(
  () => props.input,
  (newValue) => {
    keyboard.value.setInput(newValue);
  }
);
onMounted(() => {
  keyboard.value = new Keyboard({
    onChange,
    onKeyPress,
    layout: {
      default: [
        "1 2 3 4 5 6 7 8 9 0",
        "q w e r t y u i o p",
        "a s d f g h j k l",
        "{shift} z x c v b n m {bksp}",
        ". {space} -",
      ],
      shift: [
        "1 2 3 4 5 6 7 8 9 0",
        "Q W E R T Y U I O P",
        "A S D F G H J K L",
        "{shift} Z X C V B N M {bksp}",
        ". {space} -",
      ],
    },
    buttonTheme: [
      {
        class: "secondary-key",
        buttons: "{shift} {bksp}",
      },
      {
        class: "space-key",
        buttons: "{space}",
      },
    ],
    display: {
      "{space}": "space",
      "{bksp}": "⌫",
      "{shift}": "⇧",
    },
  });
});
</script>

<template>
  <div class="keyboard simple-keyboard"></div>
</template>

<style scoped lang="scss">
.hg-theme-default {
  background: transparent;
  display: flex;
  justify-content: center;

  :deep(.hg-rows) {
    display: inline-flex;
    gap: var(--spacing-sm);
    flex-direction: column;
    align-items: center;
  }

  :deep(.hg-row) {
    gap: var(--spacing-sm);
    justify-content: center;
    margin-bottom: 0 !important;
    width: 100%;
  }

  :deep(.hg-button) {
    background: var(--keyboard-default-surface);
    color: var(--keyboard-default-text);
    font-size: var(--font-size-body-large);
    border-radius: var(--border-radius-medium);
    width: var(--default-button-height) !important;
    height: var(--default-button-height);
    flex-grow: 0;
  }

  :deep(.hg-button.secondary-key) {
    background: var(--keyboard-secondary-surface);
    color: var(--keyboard-secondary-text);
    padding: var(--spacing-base) var(--spacing-base-sm);
    flex: 1 0 0;
    width: 76px !important;
  }

  :deep(.hg-button.space-key) {
    min-width: var(--default-button-height);
    flex: 1 0 0;
  }

  :deep(.hg-button.shift-active) {
    background: var(--keyboard-active-surface);
    color: var(--keyboard-active-text);
  }

  :deep(.hg-button:hover) {
    background: var(--keyboard-hover-surface);
    color: var(--keyboard-hover-text);
  }

  :deep(.hg-button:active) {
    background: var(--keyboard-active-surface);
    color: var(--keyboard-active-text);
  }

  :deep(.hg-button img) {
    display: block;
  }
}
</style>
